import * as React from 'react';
import {FC, JSX, useEffect, useRef} from "react";
import Profile from "../../../Model/Profile.ts";
import {ColumnDefinition, TabulatorFull as Tabulator} from "tabulator-tables";
import clsx from "clsx";
import css from '../EpidStatus.module.css';
import {EpidActive as EpidActiveModel} from "../model/EpidActive.ts";
import {EpidStatus as EpidStatusModel} from "../model/EpidStatus.ts";
import {EpidPublicon4 as EpidPublicon4Model} from "../model/EpidPublicon4.ts";
import {EpidPublicon8 as EpidPublicon8Model} from "../model/EpidPublicon8.ts";
import {EpidConfig as EpidConfigApi} from "../api/EpidConfig.ts";
import {EpidStatus as EpidStatusApi} from "../api/EpidStatus.ts";
import {Decorator} from "../../../../Components/Tabulator/Decorator.tsx";

export interface EpidStatusTableProps {
    token: string;
    profile: Profile;
}

const EpidStatusTable: FC<EpidStatusTableProps> = (props): JSX.Element => {
    const tableRef = useRef<HTMLDivElement>(null);
    const tabulatorRef = useRef<Tabulator | null>(null);
    const isTableBuilt = useRef<boolean>(false);

    const [data, setData] = React.useState<EpidStatusModel[]>();

    const activeCellClick = async (_e: any, cell: any) => {
        const rowElement = cell.getRow().getElement();
        const cellElement = cell.getElement();

        Decorator.rowUpdateStart(rowElement, cellElement);

        try {
            const epidConfigApi = new EpidConfigApi(props.token, props.profile);
            const request: EpidActiveModel = {
                profileId: cell.getRow().getIndex(),
                active: !cell.getValue()
            }
            const response = await epidConfigApi.patchActive(request) as EpidStatusModel;
            cell.setValue(response.active);

            Decorator.rowUpdateSuccess(rowElement, cellElement);
        } catch (error) {
            Decorator.rowUpdateException(rowElement, cellElement, "Nie udało się zmienić wartości Active dla: " + cell.getRow().getData().epid);
        }
    }

    const publicon4CellClick = async (_e: any, cell: any) => {
        const rowElement = cell.getRow().getElement();
        const cellElement = cell.getElement();
        Decorator.rowUpdateStart(rowElement, cellElement);

        try {
            const epidConfigApi = new EpidConfigApi(props.token, props.profile);
            const request: EpidPublicon4Model = {
                profileId: cell.getRow().getIndex(),
                active: !cell.getValue()
            }
            const response = await epidConfigApi.patchPublicon4(request) as EpidStatusModel;
            cell.setValue(response.publicon4);

            Decorator.rowUpdateSuccess(rowElement, cellElement);
        } catch (error) {
            Decorator.rowUpdateException(rowElement, cellElement, "Nie udało się zmienić wartości Publicon4 dla: " + cell.getRow().getData().epid);
        }
    };

    const publicon8CellClick = async (_e: any, cell: any) => {
        const rowElement = cell.getRow().getElement();
        const cellElement = cell.getElement();

        Decorator.rowUpdateStart(rowElement, cellElement);

        try {
            const epidConfigApi = new EpidConfigApi(props.token, props.profile);
            const request: EpidPublicon8Model = {
                profileId: cell.getRow().getIndex(),
                active: !cell.getValue()
            }
            const response = await epidConfigApi.patchPublicon8(request) as EpidStatusModel;
            cell.setValue(response.publicon8);

            Decorator.rowUpdateSuccess(rowElement, cellElement);
        } catch (error) {
            Decorator.rowUpdateException(rowElement, cellElement, "Nie udało się zmienić wartości Active dla: " + cell.getRow().getData().epid);
        }
    }

    const columns: ColumnDefinition[] = [
        {
            title: "EPID",
            field: "epid",
            resizable: true,
            width: 120
        }, {
            title: "Aktywny",
            field: "active",
            formatter: "tickCross",
            hozAlign: "center",
            width: 100,
            resizable: true,
            cellClick: activeCellClick,
            cssClass: "editable-cell",
        }, {
            title: "Nazwa firmy",
            field: "companyName",
            resizable: true,
        }, {
            title: "Zasięgi",
            field: "addressCount",
            width: 100,
            resizable: true,
            hozAlign: "right",

        }, {
            title: "PUBLIKLON 4",
            field: "publicon4",
            formatter: "tickCross",
            width: 130,
            resizable: true,
            hozAlign: "center",
            cellClick: publicon4CellClick,
            cssClass: "editable-cell",
        }, {
            title: "PUBLIKLON 8",
            field: "publicon8",
            formatter: "tickCross",
            width: 130,
            resizable: true,
            hozAlign: "center",
            cellClick: publicon8CellClick,
            cssClass: "editable-cell",
        }
    ];

    useEffect(() => {
        const fetchData = async () => {
            console.log('Init fetchData');
            try {
                const epidStatusApi = await new EpidStatusApi(props.token, props.profile).allEpidStatus();
                setData(epidStatusApi);
            } catch (error) {
                console.error('Error fetching epid status:', error);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        let frameId: number | null = null;
        isTableBuilt.current = true;

        if (tableRef.current) {
            frameId = window.requestAnimationFrame(() => {
                if (!tableRef.current) {
                    return;
                }
                const tabulator = new Tabulator(tableRef.current, {
                    data: data,
                    columns,
                    layout: "fitColumns",
                    resizableColumnFit: true,
                    movableColumns: true,
                    reactiveData: true,
                    index: "profileId",
                });

                tabulator.on("tableBuilt", () => {
                    isTableBuilt.current = true;
                    tabulatorRef.current = tabulator;
                });

            });
        }

        return () => {

            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }
            isTableBuilt.current = false;
            if (tabulatorRef.current) {
                tabulatorRef.current.destroy();
                tabulatorRef.current = null;
            }
        };

    }, []);

    useEffect(() => {
        if (isTableBuilt.current && tabulatorRef.current && (tabulatorRef.current) as any) {
            tabulatorRef.current.setData(data).catch((err) => {
                console.warn("Tabulator zignorował asynchroniczne podstawienie danych:", err);
            });
        }
    }, [data]);

    return (
        <div className={clsx(css.epidStatus)}>
            <div className={clsx(css.tableContainer)} ref={tableRef}></div>
        </div>
    )
}
export default EpidStatusTable